import { pipe } from "@mobily/ts-belt";
import { EitherAsync } from "purify-ts";
import { match, P } from "ts-pattern";
import { type GenericSchema, parse } from "valibot";

import type { HttpCodeErrors, ReponseSimplifiee } from "./types/reseau";

import { ENTETE_WC_NONCE } from "../constantes/api.ts";
import {
  BadRequestError,
  ErreurInconnue,
  ForbiddenError,
  leveBadRequestError,
  leveErreur,
  leveNotFoundError,
  leveUnauthorizedError,
  NotFoundError,
  ServerError,
  UnauthorizedError,
  type UnknownError,
} from "./erreurs.ts";
import { estWCError } from "./schemas/api/erreurs.ts";

// Types

type ArgumentsDeleteBackendWC = {
  authString?: string;
  /** Une chaîne de caractères généré par le backend et à joindre à chaque requête pour l'authentifier. */
  nonce: string;
  /** La route, ou chemin, de l'API à demander au backend. */
  route: string;
};

type ArgumentsGetBackendWC = {
  authString?: string;
  /** Une chaîne de caractères généré par le backend et à joindre à chaque requête pour l'authentifier. */
  nonce: string;
  /** La route, ou chemin, de l'API à demander au backend. */
  route: string;
  /** Paramètres de recherche (« query args ») à injecter dans l'URL de la requête. */
  searchParams?: string;
};

type ArgumentsPostBackendWC = {
  authString?: string;
  /** Le corps de la requête (ses arguments). */
  corps: BodyInit;
  /** Une chaîne de caractères généré par le backend et à joindre à chaque requête pour l'authentifier. */
  nonce: string;
  /** La route, ou chemin, de l'API à demander au backend. */
  route: string;
};

// fetch

export const getBackend = (args: ArgumentsGetBackendWC): Promise<Response> =>
  fetch(
    args.route,
    {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        [ENTETE_WC_NONCE]: args.nonce,
        // Ajoute conditionnellement l'en-tête « Authorization »
        ...(args.authString && { Authorization: `Basic ${args.authString}` }),
      },
      method: "GET",
      mode: "same-origin",
      signal: AbortSignal.timeout(5000),
    },
  );

export const getBackendAvecParametresUrl = (args: ArgumentsGetBackendWC): Promise<Response> =>
  fetch(
    `${args.route}?${args.searchParams}`,
    {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        [ENTETE_WC_NONCE]: args.nonce,
        // Ajoute conditionnellement l'en-tête « Authorization »
        ...(args.authString && { Authorization: `Basic ${args.authString}` }),
      },
      method: "GET",
      mode: "same-origin",
      signal: AbortSignal.timeout(5000),
    },
  );

export const deleteBackend = (args: ArgumentsDeleteBackendWC): Promise<Response> =>
  fetch(
    args.route,
    {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        [ENTETE_WC_NONCE]: args.nonce,
        // Ajoute conditionnellement l'en-tête « Authorization »
        ...(args.authString && { Authorization: `Basic ${args.authString}` }),
      },
      method: "DELETE",
      mode: "same-origin",
      signal: AbortSignal.timeout(5000),
    },
  );

export const postBackend = (args: ArgumentsPostBackendWC): Promise<Response> =>
  fetch(
    args.route,
    {
      body: args.corps,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        [ENTETE_WC_NONCE]: args.nonce,
        // Ajoute conditionnellement l'en-tête « Authorization »
        ...(args.authString && { Authorization: `Basic ${args.authString}` }),
      },
      method: "POST",
      mode: "same-origin",
      signal: AbortSignal.timeout(5000),
    },
  );

export const eitherAsyncFetch = (f: Promise<Response>): EitherAsync<DOMException | TypeError, Response> =>
  EitherAsync<DOMException | TypeError, Response>(async () => await f);

// TODO: Ne traite pas du tout les Erreurs
// TODO: Utiliser un Either
export const traiteReponseBackendWCSelonCodesHTTP = <R, S extends GenericSchema<R>>(
  corpsReponse: unknown,
  schemaReponse: S,
): R =>
  match(corpsReponse)
    // Réponses problématiques
    .with({ body: P.select(), status: 400 }, estWCError, leveBadRequestError)
    .with({ body: P.select(), status: 401 }, estWCError, leveUnauthorizedError)
    .with({ body: P.select(), status: 404 }, estWCError, leveNotFoundError)
    // Réponse OK (201)
    .with(P._, corpsOkInconnu => parse<S>(schemaReponse, corpsOkInconnu))
    // Réponses inconnues
    .otherwise(e => pipe(e, ErreurInconnue, leveErreur<UnknownError>));

// Réponses Simplifiées
export const creeReponseSimplifiee = async (reponse: Response): Promise<ReponseSimplifiee> => {
  return {
    body: await reponse.json(),
    status: reponse.status,
  };
};

export const traiteErreursBackendWooCommerce = (rs: ReponseSimplifiee): HttpCodeErrors => {
  return match(rs)
    .with({ status: 400 }, () => new BadRequestError())
    .with({ status: 401 }, () => new UnauthorizedError())
    .with({ status: 403 }, () => new ForbiddenError())
    .with({ status: 404 }, () => new NotFoundError())
    .with({ status: 500 }, () => new ServerError())
    .otherwise(rs => new Error(String(rs.status)));
};
